import { Box, Flex } from '@chakra-ui/react';
import { Field, FieldArray } from 'formik';
import { FaPlus } from 'react-icons/fa';
import { useEffect } from 'react';
import { IconButton } from '@/components/ats';
import {
  validatePrimitiveField,
  getEmptyItem,
  isMaxItemsReached as checkMaxItems,
  ArrayHeader,
  ArrayItemContainer,
  FieldRenderer,
  MaxItemsMessage,
} from './arrayFieldHelpers';

export const ArrayField = ({ item = {}, formik = {} }) => {
  const { name: key, schema } = item;
  const { values, setFieldValue } = formik;
  const items = values[key] || [];
  const itemType = schema.item_type;
  const properties = itemType.properties;
  const isPrimitiveArray = !properties && itemType.type;
  const isMaxItemsReached = checkMaxItems(schema, items?.length);

  // Clear field value when all items are removed
  useEffect(() => {
    if (items.length === 0) {
      setFieldValue(key, undefined);
    }
  }, [items.length, setFieldValue, key]);

  // Handle primitive arrays
  if (isPrimitiveArray) {
    return (
      <FieldArray name={key}>
        {({ push, remove }) => (
          <Box mb={6}>
            <ArrayHeader
              label={schema?.label}
              required={schema?.required}
              description={schema?.description}
            />

            {items.map((_, idx) => (
              <Field
                name={`${key}.${idx}`}
                key={idx}
                validate={(value) => validatePrimitiveField(value, itemType)}
              >
                {({ field, form, meta }) => (
                  <ArrayItemContainer onRemove={() => remove(idx)}>
                    <FieldRenderer
                      field={field}
                      form={form}
                      meta={meta}
                      fieldSchema={{
                        ...itemType,
                        label: itemType?.label || schema?.label,
                      }}
                      formik={formik}
                      itemType={itemType}
                      schema={schema}
                      isRequired={itemType?.required}
                    />
                  </ArrayItemContainer>
                )}
              </Field>
            ))}

            {isMaxItemsReached && (
              <MaxItemsMessage maxItems={schema?.max_items} />
            )}

            <IconButton
              icon={FaPlus}
              text={`Add ${itemType?.label || schema?.label}`}
              type="button"
              onClick={() =>
                push(getEmptyItem(isPrimitiveArray, itemType, properties))
              }
              disabled={isMaxItemsReached}
              cursor={isMaxItemsReached ? 'not-allowed' : 'pointer'}
            />
          </Box>
        )}
      </FieldArray>
    );
  }

  // Handle object arrays (with properties)
  return (
    <FieldArray name={key}>
      {({ push, remove }) => (
        <Box mb={6}>
          <ArrayHeader
            label={schema?.label}
            required={schema?.required}
            description={schema?.description}
          />

          {items?.map((_, idx) => (
            <ArrayItemContainer key={idx} onRemove={() => remove(idx)}>
              <Flex direction="column" gap={4}>
                {Object.entries(properties).map(([propName, propSchema]) => (
                  <Field name={`${key}.${idx}.${propName}`} key={propName}>
                    {({ field, form, meta }) => (
                      <FieldRenderer
                        field={field}
                        form={form}
                        meta={meta}
                        fieldSchema={propSchema}
                        formik={formik}
                        itemType={itemType}
                        schema={schema}
                        isRequired={propSchema?.required}
                      />
                    )}
                  </Field>
                ))}
              </Flex>
            </ArrayItemContainer>
          ))}

          {isMaxItemsReached && (
            <MaxItemsMessage maxItems={schema?.max_items} />
          )}

          <IconButton
            icon={FaPlus}
            text={`Add ${schema?.label}`}
            type="button"
            onClick={() =>
              push(getEmptyItem(isPrimitiveArray, itemType, properties))
            }
            disabled={isMaxItemsReached}
            cursor={isMaxItemsReached ? 'not-allowed' : 'pointer'}
          />
        </Box>
      )}
    </FieldArray>
  );
};
